// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { ReactNode } from "react";
import { FormLabel, FormMessage } from "@/components/ui/form";
import { cn } from "@/lib/utils";

interface FormLabelRowProps {
  label: ReactNode;
  /** Optional trailing content (e.g. a "Not required" hint or a tooltip), shown after the message. */
  aside?: ReactNode;
  className?: string;
}

/**
 * Standard label row for a form field: the label on the left, with the validation message (and any
 * optional `aside`) aligned to the right. The message wraps right-aligned and top-aligned with the
 * label, so a long error never crowds or overlaps the label. Must be rendered inside a `FormItem`.
 *
 * The message's line box is pinned to 14px to match the label (`text-sm leading-none`). Without this
 * the message's taller line box would make the `items-start` row grow by 1px the moment an error
 * appears — a visible layout shift when toggling between error and non-error states.
 */
export function FormLabelRow({ label, aside, className }: FormLabelRowProps) {
  return (
    <div className={cn("flex items-start justify-between gap-3", className)}>
      <FormLabel className="shrink-0">{label}</FormLabel>
      <div className="flex min-w-0 items-center gap-2">
        <FormMessage className="text-xs leading-[14px] text-right" />
        {aside}
      </div>
    </div>
  );
}
